import React, { useState } from "react";
import { Button } from "@mui/material";
import { Inline } from "components/layout/reusableLayouts";
import { useTranslation } from "react-i18next";
import { FormikProps } from "formik";
import UserStatus from "enums/UserStatus";
import Role from "enums/Role";
import { FormikAddUser } from "hooks/useUserProfileData";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import CustomFieldParentType from "enums/CustomFieldParentType";
import {
  RemoveUserModal,
  ResendUserInviteModal,
  ResetUserPasswordModal,
} from "components/common/users/UserModules";
import Summary from "components/common/Summary";
import { fullName } from "utils/fullName";

interface DisplayUserInformationProps {
  handleClickEdit: () => void;
  formik: FormikProps<FormikAddUser>;
  handleUpdateUser?: (error?: string) => void;
  handleClickReactivateUser?: () => void;
}

const PlatformDisplayUserInformation: React.FC<DisplayUserInformationProps> = (
  props,
) => {
  const {
    handleClickEdit,
    handleUpdateUser,
    handleClickReactivateUser,
    formik,
  } = props;

  const { t } = useTranslation();

  const [openResetUserPasswordModal, setOpenResetUserPasswordModal] =
    useState(false);
  const [openRemoveUserModal, setOpenRemoveUserModal] = useState(false);
  const [openResendUserInviteModal, setOpenResendUserInviteModal] =
    useState(false);

  const handleclickResetPassword = () => {
    setOpenResetUserPasswordModal(true);
  };

  const handleClickRemoveUser = () => {
    setOpenRemoveUserModal(true);
  };

  const handleClickResendInvite = () => {
    setOpenResendUserInviteModal(true);
  };

  const { filteredCustomFieldSummaryItems } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.USER,
    customFields: formik.values.customFields,
  });

  const summaryItems = [
    {
      title: t("Account"),
      value: formik.values.company?.businessLegalName,
    },
    {
      title: t("Name"),
      value: fullName(formik.values),
    },
    {
      title: t("Email Address"),
      value: formik.values?.email,
      fullWidth: true,
    },
    {
      title: t("Role"),
      value: formik.values?.role,
    },
    ...filteredCustomFieldSummaryItems,
  ];

  return (
    <>
      <Summary
        items={summaryItems}
        disableContainer
        children={
          formik.values.id && (
            <Inline gap="var(--space-3)" justify="center">
              {formik.values.role !== Role.Cardholder &&
                formik.values.status !== UserStatus.PENDING && (
                  <Button
                    variant="danger-outlined"
                    size="small"
                    onClick={handleclickResetPassword}
                  >
                    {t("Reset Password")}
                  </Button>
                )}

              {formik.values.role !== Role.Cardholder &&
                formik.values.status === UserStatus.PENDING && (
                  <Button
                    variant="default-outlined"
                    size="small"
                    onClick={handleClickResendInvite}
                  >
                    {t("Resend Invite")}
                  </Button>
                )}

              {formik.values.status !== UserStatus.INACTIVE && (
                <Button
                  variant="danger-filled"
                  size="small"
                  onClick={handleClickRemoveUser}
                >
                  {t("Deactivate")}
                </Button>
              )}

              {formik.values.status !== UserStatus.INACTIVE && (
                <Button
                  variant="primary-filled"
                  size="small"
                  onClick={handleClickEdit}
                >
                  {t("Edit User Info")}
                </Button>
              )}

              {formik.values.status === UserStatus.INACTIVE &&
                handleClickReactivateUser && (
                  <Button
                    variant="primary-filled"
                    size="small"
                    onClick={() =>
                      formik.values.id && handleClickReactivateUser()
                    }
                  >
                    {t("Reactivate")}
                  </Button>
                )}
            </Inline>
          )
        }
      />

      {formik.values?.id && (
        <RemoveUserModal
          openRemoveUserModal={openRemoveUserModal}
          setOpenRemoveUserModal={setOpenRemoveUserModal}
          userId={formik.values.id}
          userEmail={formik.values?.email}
          handleUpdateUser={handleUpdateUser}
        />
      )}

      <ResetUserPasswordModal
        openResetUserPasswordModal={openResetUserPasswordModal}
        setOpenResetUserPasswordModal={setOpenResetUserPasswordModal}
        userEmail={formik.values?.email}
      />

      <ResendUserInviteModal
        openResendUserInviteModal={openResendUserInviteModal}
        setOpenResendUserInviteModal={setOpenResendUserInviteModal}
        userEmail={formik.values?.email}
        handleUpdateUser={handleUpdateUser}
      />
    </>
  );
};

export default PlatformDisplayUserInformation;
